import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { CompanyConfigResponse } from 'src/app/models/company.model';
import { ListData } from 'src/app/models/price-list.model';
import { ResponseObject } from 'src/app/models/response-object.model';
import { Regimes } from 'src/app/models/vendor.model';
import { WarehouseResponse } from 'src/app/models/warehouse.model';
import { CompanyService } from 'src/app/services/company.service';
import { PriceService } from 'src/app/services/price.service';
import { VendorService } from 'src/app/services/vendor.service';
import { WarehouseService } from 'src/app/services/warehouse.service';

@Component({
  selector: 'app-general-configuration',
  templateUrl: './general-configuration.component.html',
  styleUrls: ['./general-configuration.component.scss']
})
export class GeneralConfigurationComponent implements OnInit {
  regimes: Regimes[] = [];
  warehouses: WarehouseResponse[] = [];
  lists: ListData[] = [];
  originalConfigs: CompanyConfigResponse[] = [];
  newConfigs: Record<number, CompanyConfigResponse> = {};

  failedStart = false;
  loadingServer = true;
  loading = false;
  loadingConfig: { [key: number]: boolean } = {};

  generalConfigs: CompanyConfigResponse[] = [];
  fiscalConfigs: CompanyConfigResponse[] = [];
  taxesConfigs: CompanyConfigResponse[] = [];
  purchaseOrderConfigs: CompanyConfigResponse[] = [];
  ordersConfigs: CompanyConfigResponse[] = [];
  originals: CompanyConfigResponse[] = [];
  columnOriginalConfigs: CompanyConfigResponse[][] = [];
  columnGeneralConfigs: CompanyConfigResponse[][] = [];
  columnFiscalConfigs: CompanyConfigResponse[][] = [];
  columnTaxesConfigs: CompanyConfigResponse[][] = [];
  columnPurchaseOrderConfigs: CompanyConfigResponse[][] = [];
  columnOrderConfigs: CompanyConfigResponse[][] = [];

  constructor(
    private companyService: CompanyService,
    private vendorService: VendorService,
    private warehouseService: WarehouseService,
    private priceService: PriceService,
    private toastService: ToastrService
  ) {

  }

  ngOnInit(): void {
    this.getStartData();
  }

  getStartData() {
    this.loadingServer = true;
    this.vendorService.getRegimes().pipe(
      concatMap((response: ResponseObject) => {
        this.regimes = response.data;
        return this.warehouseService.getWarehouses();
      }),
      concatMap((response: ResponseObject) => {
        this.warehouses = response.data;
        return this.priceService.getPriceLists();
      }),
      concatMap((response: ResponseObject) => {
        this.lists = response.data;
        return this.companyService.getAllConfig();
      })
    ).subscribe(
      {
        next: (response: ResponseObject) => {
          this.originalConfigs = response.data;
          this.newConfigs = {};
          this.originalConfigs.forEach(element => {
            this.newConfigs[element.id] = { ...element }
          });
          this.resetConfigsType();
          this.loadingServer = false;
        },
        error: (error: HttpErrorResponse) => {
          this.failedStart = true;
          this.loadingServer = false;
          this.toastService.error(error.error.message);
        }
      }
    );
  }

  getAllConfig() {
    this.companyService.getAllConfig().subscribe(
      {
        next: (response: ResponseObject) => {
          this.originalConfigs = response.data;
          this.newConfigs = {};
          this.originalConfigs.forEach(element => {
            this.newConfigs[element.id] = { ...element }
          });
          this.resetConfigsType();
        },
        error: (error: HttpErrorResponse) => {
          this.toastService.error(error.error.message);
        }
      }
    );
  }

  resetConfigsType() {
    this.originals = [];
    this.generalConfigs = [];
    this.fiscalConfigs = [];
    this.taxesConfigs = [];
    this.ordersConfigs = [];
    this.purchaseOrderConfigs = [];
    this.columnOriginalConfigs = [];
    this.columnGeneralConfigs = [];
    this.columnFiscalConfigs = [];
    this.columnTaxesConfigs = [];
    this.columnPurchaseOrderConfigs = [];
    this.columnOrderConfigs = [];

    this.originalConfigs.forEach(element => {
      if (element.management == 0 && element.type == 'boolean') {
        this.generalConfigs.push(element);
      } else if (element.management == 1 && element.type == 'boolean') {
        this.fiscalConfigs.push(element);
      } else if (element.management == 2 && element.type == 'boolean') {
        this.taxesConfigs.push(element);
      } else if (element.management == 3 && element.type == 'boolean') {
        this.ordersConfigs.push(element);
      } else if (element.management == 4 && element.type == 'boolean') {
        this.purchaseOrderConfigs.push(element);
      } else {
        this.originals.push(element);
      }
    });

    this.originals.sort((a, b) => a.displayName.localeCompare(b.displayName));
    this.generalConfigs.sort((a, b) => a.displayName.localeCompare(b.displayName));
    this.fiscalConfigs.sort((a, b) => a.displayName.localeCompare(b.displayName));
    this.taxesConfigs.sort((a, b) => a.displayName.localeCompare(b.displayName));
    this.purchaseOrderConfigs.sort((a, b) => a.displayName.localeCompare(b.displayName));
    this.ordersConfigs.sort((a, b) => a.displayName.localeCompare(b.displayName));

    this.columnOriginalConfigs = this.splitColumns(this.originals);
    this.columnGeneralConfigs = this.splitColumns(this.generalConfigs);
    this.columnFiscalConfigs = this.splitColumns(this.fiscalConfigs);
    this.columnTaxesConfigs = this.splitColumns(this.taxesConfigs);
    this.columnPurchaseOrderConfigs = this.splitColumns(this.purchaseOrderConfigs);
    this.columnOrderConfigs = this.splitColumns(this.ordersConfigs);
  }

  private splitColumns(arr: CompanyConfigResponse[]): CompanyConfigResponse[][] {
    const midpoint = Math.ceil(arr.length / 2);
    return [arr.slice(0, midpoint), arr.slice(midpoint)];
  }

  changeConfig(oldConfig, newValue) {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.loadingConfig[oldConfig.id] = true;
    this.companyService.changeConfig(oldConfig.id, newValue).subscribe(
      {
        next: (response: ResponseObject) => {
          this.loading = false;
          this.loadingConfig[oldConfig.id] = false;
          this.toastService.success("Se ha cambiado correctamente la configuracion");
          this.getAllConfig();
        },
        error: (error: HttpErrorResponse) => {
          this.loading = false;
          this.loadingConfig[oldConfig.id] = false;
          this.toastService.error(error.error.message);
        }
      }
    )
  }

  isChanged() {
    var changed = [];
    for (let i = 0; i < this.originalConfigs.length; i++) {
      const original = this.originalConfigs[i];
      const newChange = this.newConfigs[original.id];
      if (original.value != newChange.value) {
        changed.push(original.id);
      }
    }
    return changed.length != 0;
  }

  async changeConfigsSequentially() {
    this.loading = true;
    this.loadingConfig[-1] = true;
    for (let i = 0; i < this.originalConfigs.length; i++) {
      const original = this.originalConfigs[i];
      const newChange = this.newConfigs[i];
      if (original.value != newChange.value) {
        await this.changeMultipleConfig(original, newChange.value).toPromise(); // Convert Observable to Promise
      }
    }
    this.loading = false;
    this.loadingConfig[-1] = false;
    this.getAllConfig();
    this.toastService.success("Se han cambiado correctamente las configuraciones realizadas");
  }

  changeMultipleConfig(oldConfig, newValue): Observable<any> {
    return new Observable(observer => {
      this.loadingConfig[oldConfig.id] = true;
      this.companyService.changeConfig(oldConfig.id, newValue).subscribe({
        next: (response: ResponseObject) => {
          this.loadingConfig[oldConfig.id] = false;
          observer.next(response);
          observer.complete();
        },
        error: (error: HttpErrorResponse) => {
          this.loadingConfig[oldConfig.id] = false;
          this.toastService.error(error.error.message);
          observer.error(error);
        }
      });
    });
  }

}
